import React from 'react';
import type { StatusTone } from '@/domain/warehouse';
import './RecordLayout.css';

/**
 * Building blocks for Warehouse and Invoicing record pages, composed from the classes the
 * Installation and Event records already use (information tabs, summary rail, ticket strip).
 * Installation and Event pages keep their own markup; these exist so the new records share one.
 */

type RecordOverviewLayoutProps = {
  children: React.ReactNode;
  /** The summary rail (an <aside>), shown beside the sections on wide screens and below them on narrow ones. */
  rail?: React.ReactNode;
  className?: string;
};

export function RecordOverviewLayout({ children, rail, className }: RecordOverviewLayoutProps) {
  return (
    <div className={className ? `event-current-overview-layout ${className}` : 'event-current-overview-layout'}>
      {children}
      {rail}
    </div>
  );
}

export type RecordSectionTab = {
  key: string;
  label: string;
  /** Shown as a small count after the label ("Lines 7"). */
  count?: number | null;
};

type RecordSectionTabsProps = {
  tabs: ReadonlyArray<RecordSectionTab>;
  value: string;
  onChange: (key: string) => void;
  /** e.g. "Transfer sections". */
  ariaLabel: string;
  /** id of the panel; tabs point at it with aria-controls. */
  panelId: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  children: React.ReactNode;
  className?: string;
};

/**
 * Record sections: the information tab strip on desktop, and on phones (≤640px) a section
 * select with the collapse button, as the record boards show.
 */
export function RecordSectionTabs({
  tabs,
  value,
  onChange,
  ariaLabel,
  panelId,
  collapsed,
  onToggleCollapsed,
  children,
  className,
}: RecordSectionTabsProps) {
  const active = tabs.find((tab) => tab.key === value) ?? tabs[0];
  const activeLabel = active?.label ?? '';
  const toggleLabel = `${collapsed ? 'Expand' : 'Collapse'} ${activeLabel}`;
  const handleSelect = React.useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
  }, [onChange]);

  const toggle = (
    <button
      type="button"
      className="btn subtle event-detail-active-panel-toggle"
      aria-expanded={!collapsed}
      aria-controls={panelId}
      aria-label={toggleLabel}
      title={toggleLabel}
      onClick={onToggleCollapsed}
    >
      <span className="event-detail-active-panel-toggle__label">{collapsed ? 'Expand' : 'Collapse'}</span>
      <span className="event-detail-active-panel-toggle__chevron" aria-hidden="true" />
    </button>
  );

  return (
    <div
      className={[
        'event-detail-information',
        'event-detail-information--current',
        'record-sections',
        collapsed ? 'event-detail-information--collapsed' : null,
        className,
      ].filter(Boolean).join(' ')}
    >
      <div className="event-detail-information__bar record-sections__bar">
        <nav className="event-detail-information__tabs" aria-label={ariaLabel} role="tablist">
          {tabs.map((tab) => {
            const selected = tab.key === active?.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`${panelId}-tab-${tab.key}`}
                aria-selected={selected}
                aria-controls={panelId}
                className={`event-detail-information__tab${selected ? ' event-detail-information__tab--active' : ''}`}
                onClick={() => onChange(tab.key)}
              >
                {tab.label}
                {tab.count != null ? (
                  <span className="record-sections__count" aria-label={`${tab.count} ${tab.label.toLowerCase()}`}>
                    {tab.count.toLocaleString()}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
        {toggle}
      </div>
      <div className="event-mobile-tab-switcher record-sections__switcher">
        <select
          className="event-mobile-tab-switcher__select"
          value={active?.key ?? ''}
          onChange={handleSelect}
          aria-label={`${ariaLabel}: choose a section`}
        >
          {tabs.map((tab) => (
            <option key={tab.key} value={tab.key}>
              {tab.count != null ? `${tab.label} (${tab.count.toLocaleString()})` : tab.label}
            </option>
          ))}
        </select>
        {toggle}
      </div>
      {!collapsed ? (
        <section
          id={panelId}
          className="section-shell event-detail-information__panel"
          role="tabpanel"
          aria-label={activeLabel}
        >
          {children}
        </section>
      ) : null}
    </div>
  );
}

export type RecordFact = {
  label: string;
  value: React.ReactNode;
};

type RecordSummaryCardProps = {
  /** Small heading ("Vendor", "Schedule"). */
  title?: string;
  /** A big figure at the top, e.g. the order total. */
  hero?: { label: string; value: React.ReactNode } | null;
  /** A name or title under the heading ("Midwest Pro Audio Supply"). */
  lead?: React.ReactNode;
  facts?: ReadonlyArray<RecordFact>;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  ariaLabel?: string;
  className?: string;
};

/** A summary-rail card in the record style (totals hero, fact lines, actions). */
export function RecordSummaryCard({
  title,
  hero,
  lead,
  facts,
  actions,
  children,
  ariaLabel,
  className,
}: RecordSummaryCardProps) {
  return (
    <section
      className={['external-service-card', 'record-summary-card', hero ? 'external-service-card--totals' : null, className]
        .filter(Boolean)
        .join(' ')}
      aria-label={ariaLabel ?? title}
    >
      {title ? <h2 className="record-summary-card__title">{title}</h2> : null}
      {hero ? (
        <div className="external-service-total-hero">
          <span>{hero.label}</span>
          <strong>{hero.value}</strong>
        </div>
      ) : null}
      {lead ? <p className="record-summary-card__lead">{lead}</p> : null}
      {facts && facts.length ? (
        <div className="external-service-total-lines record-summary-card__facts">
          {facts.map((fact) => (
            <div key={fact.label}>
              <span>{fact.label}</span>
              <strong>{fact.value}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {children}
      {actions ? <div className="record-summary-card__actions">{actions}</div> : null}
    </section>
  );
}

export type RecordKpi = {
  key: string;
  label: string;
  value: React.ReactNode;
  /** 'highlight' tints the cell (the headline figure); 'warn' colours the value. */
  tone?: 'highlight' | 'warn' | null;
};

type RecordKpiStripProps = {
  items: ReadonlyArray<RecordKpi>;
  /** Names the strip, e.g. "2026 performance". */
  ariaLabel: string;
  className?: string;
};

/** A row of labelled figures under a record header ("Revenue 12 months $18,930 · Utilization 22%"). */
export function RecordKpiStrip({ items, ariaLabel, className }: RecordKpiStripProps) {
  if (!items.length) return null;
  return (
    <dl className={className ? `record-kpi-strip ${className}` : 'record-kpi-strip'} aria-label={ariaLabel}>
      {items.map((item) => (
        <div key={item.key} className="record-kpi-strip__item" data-tone={item.tone ?? undefined}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

type RecordNextStepProps = {
  tone?: Extract<StatusTone, 'info' | 'ok' | 'warn'>;
  children: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
};

/** The one-line "what happens next" banner under a record header, with its action buttons. */
export function RecordNextStep({ tone = 'info', children, actions, className }: RecordNextStepProps) {
  return (
    <div className={className ? `record-next-step ${className}` : 'record-next-step'} data-tone={tone} role="status">
      <span className="record-next-step__text">{children}</span>
      {actions ? <span className="record-next-step__actions">{actions}</span> : null}
    </div>
  );
}

type RecordMobileIdentityProps = {
  code: string;
  /** The document kind ("Transfer", "Invoice"). */
  view: string;
  name: string;
};

/** The phone ticket strip's identity (code, kind badge, name), as on Event and Installation records. */
export function RecordMobileIdentity({ code, view, name }: RecordMobileIdentityProps) {
  return (
    <div className="event-service-mobile-identity">
      <span className="event-service-mobile-identity__meta">
        <span className="event-service-mobile-identity__code">{code}</span>
        <span className="event-service-mobile-identity__view">{view}</span>
      </span>
      <span className="event-service-mobile-identity__name">{name}</span>
    </div>
  );
}

type RecordPhoneActionBarProps = {
  /** The one primary action ("Receive 22 units"). */
  primary?: React.ReactNode;
  /** Small icon buttons beside it. */
  secondary?: React.ReactNode;
  ariaLabel: string;
};

/** A sticky action bar at the bottom of record pages on phones (≤640px); hidden on wider screens. */
export function RecordPhoneActionBar({ primary, secondary, ariaLabel }: RecordPhoneActionBarProps) {
  if (!primary && !secondary) return null;
  return (
    <div className="record-phone-action-bar" role="toolbar" aria-label={ariaLabel}>
      {primary ? <div className="record-phone-action-bar__primary">{primary}</div> : null}
      {secondary ? <div className="record-phone-action-bar__secondary">{secondary}</div> : null}
    </div>
  );
}
